import { Link, Outlet, Route, Routes } from 'react-router-dom'
import { BrokenLink } from './components/BrokenLink'
import { Titled } from './components/Titled'
import { AlbumLayout } from './layouts/AlbumLayout'
import { ChallengeDetailPage } from './pages/ChallengeDetailPage'
import { ChallengesPage } from './pages/ChallengesPage'
import { GalleryPage } from './pages/GalleryPage'
import { LandingPage } from './pages/LandingPage'
import { HomePage } from './marketing/HomePage'
import { MediaViewerPage } from './pages/MediaViewerPage'
import { WhoAreYouPage } from './pages/WhoAreYouPage'
import { SessionProvider } from './session/SessionProvider'
import { OwnerGate } from './owner/OwnerGate'
import { EventExportPage } from './owner/EventExportPage'
import { ForgotPasswordPage } from './owner/auth/ForgotPasswordPage'
import { NewPasswordPage } from './owner/auth/NewPasswordPage'
import { RegisterPage } from './owner/auth/RegisterPage'
import { SignInPage } from './owner/auth/SignInPage'
import { MyEventsPage } from './owner/MyEventsPage'
import { MyInvitesPage } from './owner/MyInvitesPage'
import { NewEventPage } from './owner/NewEventPage'
import { EventLayout } from './owner/EventLayout'
import { EventDashboardPage } from './owner/EventDashboardPage'
import { EventInfoPage } from './owner/EventInfoPage'
import { EventGuestsPage } from './owner/EventGuestsPage'
import { EventQrPage } from './owner/EventQrPage'
import { EventChallengesPage } from './owner/EventChallengesPage'
import { EventSettingsPage } from './owner/EventSettingsPage'

/**
 * Twee werelden onder één app.
 *
 * `/f/{eventSlug}` is de gastenkant: geen account, het invite token uit de
 * QR-code zit in het fragment. `/beheer` is de eigenaarskant: wél een account,
 * met een sessiecookie. Ze delen de tokens en de componenten, maar niet hun
 * manier van binnenkomen — en dat verschil is het hele product.
 */
export default function App() {
  return (
    <Routes>
      {/* De publieke voorpagina. Een gast komt binnen via de QR-code en een
          eigenaar via /beheer, maar wie het kale domein opent hoort te zien wat
          Confetti is — en van daar te kunnen registreren of inloggen. */}
      <Route
        path="/"
        element={
          <Titled title={null}>
            <HomePage />
          </Titled>
        }
      />

      <Route
        path="/f/:slug"
        element={
          <SessionProvider>
            <Outlet />
          </SessionProvider>
        }
      >
        <Route index element={<LandingPage />} />
        <Route path="wie-ben-je" element={<WhoAreYouPage />} />

        <Route element={<AlbumLayout />}>
          <Route path="album" element={<GalleryPage />} />
          <Route path="challenges" element={<ChallengesPage />} />
          <Route path="challenges/:challengeId" element={<ChallengeDetailPage />} />
        </Route>

        {/* Buiten de schil: de viewer neemt het hele scherm over. */}
        <Route path="album/:mediaId" element={<MediaViewerPage />} />
      </Route>

      {/* Aanmelden staat bewust búiten OwnerGate: dit zijn de schermen voor
          wie nog niet binnen is (ronde 21A t/m 21C, 21G t/m 21I). */}
      <Route
        path="/beheer/aanmelden"
        element={
          <Titled title="Aanmelden">
            <SignInPage />
          </Titled>
        }
      />
      <Route
        path="/beheer/registreren"
        element={
          <Titled title="Registreren">
            <RegisterPage />
          </Titled>
        }
      />
      <Route
        path="/beheer/wachtwoord-vergeten"
        element={
          <Titled title="Wachtwoord vergeten">
            <ForgotPasswordPage />
          </Titled>
        }
      />
      <Route
        path="/beheer/nieuw-wachtwoord/:token"
        element={
          <Titled title="Nieuw wachtwoord">
            <NewPasswordPage />
          </Titled>
        }
      />

      <Route
        path="/beheer"
        element={
          <OwnerGate>
            <Outlet />
          </OwnerGate>
        }
      >
        <Route
          index
          element={
            <Titled title="Mijn events">
              <MyEventsPage />
            </Titled>
          }
        />
        <Route
          path="nieuw"
          element={
            <Titled title="Nieuw event">
              <NewEventPage />
            </Titled>
          }
        />

        <Route
          path="uitnodigingen"
          element={
            <Titled title="Uitnodigingen">
              <MyInvitesPage />
            </Titled>
          }
        />

        <Route path="e/:eventId" element={<EventLayout />}>
          <Route
            index
            element={
              <Titled title="Overzicht">
                <EventDashboardPage />
              </Titled>
            }
          />
          <Route
            path="info"
            element={
              <Titled title="Eventinfo">
                <EventInfoPage />
              </Titled>
            }
          />
          <Route
            path="gasten"
            element={
              <Titled title="Gasten">
                <EventGuestsPage />
              </Titled>
            }
          />
          <Route
            path="exporteren"
            element={
              <Titled title="Exporteren">
                <EventExportPage />
              </Titled>
            }
          />
          <Route
            path="qr"
            element={
              <Titled title="QR-code">
                <EventQrPage />
              </Titled>
            }
          />
          <Route
            path="challenges"
            element={
              <Titled title="Challenges">
                <EventChallengesPage />
              </Titled>
            }
          />
          <Route
            path="instellingen"
            element={
              <Titled title="Instellingen">
                <EventSettingsPage />
              </Titled>
            }
          />
        </Route>
      </Route>

      {/*
        Ook de hoofdpagina komt hier uit: op / staat niets, en dat is juist —
        een gast komt binnen via de QR-code en een eigenaar via /beheer. Het pad
        stond hier eerder als platte tekst, en dan mag je het zelf overtypen.
      */}
      <Route
        path="*"
        element={
          <Titled title={null}>
            <BrokenLink
              title="Hier is niets"
              detail={
                <>
                  Open het feest via de QR-code op tafel. Ben je het bruidspaar?{' '}
                  <Link to="/beheer" className="text-accent underline underline-offset-4">
                    Ga naar beheer
                  </Link>
                  .
                </>
              }
            />
          </Titled>
        }
      />
    </Routes>
  )
}
